export const OPPORTUNITY_PRICING_VERSION = "fixed-usd-brl-v1" as const;
export const OPPORTUNITY_BASE_CURRENCY = "usd" as const;
export const OPPORTUNITY_BRL_CURRENCY = "brl" as const;
export const OPPORTUNITY_BRL_MULTIPLIER = 5;

// Stripe accepts at most eight digits in a charge amount. Because every Price
// must remain valid in both USD and BRL, the USD ladder is capped by its BRL
// counterpart and aligned to the configured $1 + $4n progression.
export const STRIPE_MAX_UNIT_AMOUNT_CENTS = 99_999_999;
export const DEFAULT_OPPORTUNITY_START_CENTS = 100;
export const DEFAULT_OPPORTUNITY_STEP_CENTS = 400;
export const OPPORTUNITY_USD_PROVIDER_MAX_CENTS = Math.floor(
  STRIPE_MAX_UNIT_AMOUNT_CENTS / OPPORTUNITY_BRL_MULTIPLIER,
);
export const DEFAULT_OPPORTUNITY_USD_CAP_CENTS =
  DEFAULT_OPPORTUNITY_START_CENTS
  + Math.floor(
    (OPPORTUNITY_USD_PROVIDER_MAX_CENTS - DEFAULT_OPPORTUNITY_START_CENTS)
      / DEFAULT_OPPORTUNITY_STEP_CENTS,
  ) * DEFAULT_OPPORTUNITY_STEP_CENTS;

export type OpportunityCurrency =
  | typeof OPPORTUNITY_BASE_CURRENCY
  | typeof OPPORTUNITY_BRL_CURRENCY;

export interface OpportunityCurrencyAmount {
  currency: OpportunityCurrency;
  amountCents: number;
}

export type OpportunityCurrencyOptions = Record<
  OpportunityCurrency,
  OpportunityCurrencyAmount
>;

export interface MarketCurrencyResponse {
  market: "BR" | "ROW";
  currency: OpportunityCurrency;
}

export function isMarketCurrencyResponse(value: unknown): value is MarketCurrencyResponse {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Partial<MarketCurrencyResponse>;
  return (
    (candidate.market === "BR" && candidate.currency === OPPORTUNITY_BRL_CURRENCY)
    || (candidate.market === "ROW" && candidate.currency === OPPORTUNITY_BASE_CURRENCY)
  );
}

export function opportunityCurrencyOptions(
  usdAmountCents: number,
): OpportunityCurrencyOptions {
  if (!Number.isSafeInteger(usdAmountCents) || usdAmountCents <= 0) {
    throw new Error("invalid_usd_amount");
  }
  const brlAmountCents = usdAmountCents * OPPORTUNITY_BRL_MULTIPLIER;
  if (
    !Number.isSafeInteger(brlAmountCents)
    || brlAmountCents > STRIPE_MAX_UNIT_AMOUNT_CENTS
  ) {
    throw new Error("brl_amount_exceeds_provider_maximum");
  }
  return {
    usd: { currency: OPPORTUNITY_BASE_CURRENCY, amountCents: usdAmountCents },
    brl: { currency: OPPORTUNITY_BRL_CURRENCY, amountCents: brlAmountCents },
  };
}

export function marketCurrencyForCountry(country: string | null | undefined): MarketCurrencyResponse {
  return country?.trim().toUpperCase() === "BR"
    ? { market: "BR", currency: OPPORTUNITY_BRL_CURRENCY }
    : { market: "ROW", currency: OPPORTUNITY_BASE_CURRENCY };
}
